// src/oura/lib/useRadioGroup.ts
//
// A pick-one row of buttons, announced and driven as a radio group (the
// WAI-ARIA pattern): one tab stop, on the checked option or else the first,
// and the arrow keys move and check together, wrapping at the ends. Buttons
// with aria-pressed read as toggles, which says any number can be on.

import { useRef, type KeyboardEvent } from "react";

/** The option an arrow, Home or End moves to from `index`, or null for any other key. */
function movedTo(key: string, index: number, count: number): number | null {
    switch (key) {
        case "ArrowRight":
        case "ArrowDown":
            return (index + 1) % count;
        case "ArrowLeft":
        case "ArrowUp":
            return (index + count - 1) % count;
        case "Home":
            return 0;
        case "End":
            return count - 1;
        default:
            return null;
    }
}

/**
 * Props for each option's `<button type="button">`; the caller puts
 * `role="radiogroup"` and an accessible name on the element around them.
 */
export function useRadioGroup<T>(options: readonly T[], value: T | undefined, onPick: (option: T) => void) {
    const buttons = useRef<Array<HTMLButtonElement | null>>([]);
    const checked = value === undefined ? -1 : options.indexOf(value);
    const tabStop = checked >= 0 ? checked : 0;

    return (i: number) => ({
        role: "radio" as const,
        "aria-checked": i === checked,
        tabIndex: i === tabStop ? 0 : -1,
        ref: (el: HTMLButtonElement | null) => {
            buttons.current[i] = el;
        },
        onClick: () => onPick(options[i]),
        onKeyDown: (e: KeyboardEvent<HTMLButtonElement>) => {
            const next = movedTo(e.key, i, options.length);
            if (next === null) return;
            e.preventDefault();
            buttons.current[next]?.focus();
            onPick(options[next]);
        },
    });
}
